// @ts-check
/* ═══════════════════════ DAILY BRIEF TIP (N1) ═══════════════════════
   Henter dagens Claude-tips via /api/brief-tip og cacher per dag.
   Faller tilbake til statiske tips hvis offline eller API feiler.
   ════════════════════════════════════════════════════════════════════ */

import { DAYS } from './data.js';
import { getDayWeather, formatBadge } from './weather.js';
import { getPortTides, hasTideData, formatTideLine } from './tides.js';

const TRIP_START = new Date('2026-07-17T00:00:00+02:00');

function isoFor(tripDay){
  const d = new Date(TRIP_START);
  d.setDate(d.getDate() + tripDay);
  return d.toISOString().slice(0, 10);
}

function cacheKey(tripDay){
  return `brief-tip:${isoFor(tripDay)}`;
}

function readCache(tripDay){
  try {
    const raw = localStorage.getItem(cacheKey(tripDay));
    if (!raw) return null;
    const { tip, ts } = JSON.parse(raw);
    // 24 t TTL
    if (Date.now() - ts > 86400000) return null;
    return tip || null;
  } catch { return null; }
}

function writeCache(tripDay, tip){
  try { localStorage.setItem(cacheKey(tripDay), JSON.stringify({ tip, ts: Date.now() })); } catch {}
}

function stripHtml(s){
  return String(s || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Returnerer dagens tips. Cache-hit gir <5 ms. Cache-miss kaller Claude.
 * Returnerer null hvis ingenting tilgjengelig — kaller faller tilbake til statisk.
 */
export async function getDailyTip(tripDay){
  if (typeof tripDay !== 'number' || tripDay < 0) return null;
  const cached = readCache(tripDay);
  if (cached) return cached;
  if (!navigator.onLine) return null;

  const day = DAYS[tripDay];
  if (!day) return null;

  // Berik prompten med ferske data hvis tilgjengelig
  const [w, tides] = await Promise.all([
    getDayWeather(tripDay),
    hasTideData(tripDay) ? getPortTides(tripDay) : null,
  ]);

  const payload = {
    tripDay,
    dayTitle: day.title,
    dayDate: day.d,
    phase: day.phase,
    weather: w ? `${formatBadge(w)}, ${w.tmin}–${w.tmax}°` : null,
    tideSummary: tides ? stripHtml(formatTideLine(tides)) : null,
  };

  try {
    const r = await fetch('/api/brief-tip', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) return null;
    const json = await r.json();
    const tip = (json?.tip || '').trim();
    if (!tip) return null;
    writeCache(tripDay, tip);
    return tip;
  } catch {
    return null;
  }
}
